import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import { StatTile } from './AdminStatTile';
import {
  getInventoryAiUsage,
  UNATTRIBUTED_RESTAURANT,
  type AiUsageSource,
  type InventoryAiUsageResponse,
  type UsageTotals,
} from '../../../services/admin/inventoryAiUsageService';
import { getAiCosts, type AiCostsResponse } from '../../../services/admin/aiCostsService';
import { getAdminRestaurantsList, type AdminRestaurant } from '../../../services/admin/adminService';

// Order the sources deliberately: the two hot voice paths first, then the batch jobs.
const SOURCE_ORDER: AiUsageSource[] = [
  'voice_agent',
  'voice_match_utterance',
  'voice_transcribe',
  'import_match',
];

/** € — values are provider list-price magnitudes; we display with € and never convert (see spec). */
const eur = (n: number) =>
  `€${n.toLocaleString('en-US', { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: n < 1 ? 4 : 2 })}`;

/** USD, not EUR — the voice section keeps its original formatter (list prices quoted in USD). */
const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: n < 1 ? 4 : 2 })}`;

const num = (n: number) => n.toLocaleString('en-US');

const currentMonth = () => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** The last 12 months, newest first — enough to look back without a date picker. */
const recentMonths = (): string[] => {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

/** Restaurants shown in the byRestaurant table before the "Unattributed" row is appended. */
const MAX_RESTAURANT_ROWS = 20;

const AdminAiCostsPage = () => {
  const { t } = useTranslation();
  const [costs, setCosts] = useState<AiCostsResponse | null>(null);
  const [voice, setVoice] = useState<InventoryAiUsageResponse | null>(null);
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [month, setMonth] = useState<string>(currentMonth());
  const [restaurantFilter, setRestaurantFilter] = useState<string>('all');
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // One month picker drives both endpoints: the universal ai_costs rollup and the voice blob.
  const load = useCallback(async (forMonth: string) => {
    setLoading(true);
    setError(null);
    try {
      const [c, v] = await Promise.all([getAiCosts(forMonth), getInventoryAiUsage(forMonth)]);
      setCosts(c);
      setVoice(v);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminAiCosts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(month);
  }, [month, load]);

  useEffect(() => {
    getAdminRestaurantsList().then(setRestaurants).catch(() => setRestaurants([]));
  }, []);

  const nameFor = useMemo(() => {
    const byId = new Map(restaurants.map((r) => [r.id, r.name]));
    return (id: string) =>
      id === UNATTRIBUTED_RESTAURANT ? t('adminAiCosts.unattributed') : byId.get(id) ?? id;
  }, [restaurants, t]);

  const featureLabel = (feature: string) =>
    t(`adminAiCosts.features.${feature}`, { defaultValue: feature });

  // ── Overview (from ai_costs) ──
  const totals = costs?.totals;
  const totalCost = totals?.cost ?? 0;
  const totalTokens = (totals?.inputTokens ?? 0) + (totals?.outputTokens ?? 0);
  const costSharePct = (c: number) => (totalCost > 0 ? Math.round((c / totalCost) * 100) : 0);
  const topFeature = costs?.byFeature[0]?.feature;

  // byRestaurant: top N by cost, then guarantee the "Unattributed" row is visible.
  const restaurantCostRows = useMemo(() => {
    const rows = costs?.byRestaurant ?? [];
    const top = rows.slice(0, MAX_RESTAURANT_ROWS);
    const unattributed = rows.find((r) => r.restaurantId == null);
    if (unattributed && !top.includes(unattributed)) top.push(unattributed);
    return top;
  }, [costs]);

  // ── Voice section (verbatim, from the inventory_ai_usage blob) ──
  const scoped = useMemo(() => {
    if (!voice) return null;
    if (restaurantFilter === 'all') {
      return { total: voice.total, bySource: voice.bySource, sessions: Object.entries(voice.bySession) };
    }
    const r = voice.byRestaurant[restaurantFilter];
    return {
      total: r?.total ?? null,
      bySource: r?.bySource ?? {},
      sessions: Object.entries(voice.bySession).filter(([, s]) => s.restaurantId === restaurantFilter),
    };
  }, [voice, restaurantFilter]);

  const restaurantRows = useMemo(() => {
    if (!voice) return [];
    return Object.entries(voice.byRestaurant).sort((a, b) => b[1].total.costUsd - a[1].total.costUsd);
  }, [voice]);

  const sessionRows = useMemo(() => {
    if (!scoped) return [];
    return [...scoped.sessions].sort((a, b) => (b[1].total.lastAt > a[1].total.lastAt ? 1 : -1)).slice(0, 25);
  }, [scoped]);

  const voiceTotal: UsageTotals | null = scoped?.total ?? null;
  const voiceTotalCost = voiceTotal?.costUsd ?? 0;
  const sourceLabel = (s: AiUsageSource) => t(`adminAiCosts.sources.${s}`);
  const voiceSharePct = (cost: number) => (voiceTotalCost > 0 ? Math.round((cost / voiceTotalCost) * 100) : 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className={cn(primaryText, 'text-2xl', semiBoldText)}>{t('adminAiCosts.title')}</h2>
            <p className={cn(secondaryText, 'mt-1', smallTextSize)}>{t('adminAiCosts.subtitle')}</p>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border-none', primaryText, smallTextSize)}
            >
              {recentMonths().map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <button
              onClick={() => load(month)}
              className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700', secondaryText, smallTextSize)}
            >
              {t('adminAiCosts.refresh')}
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className={smallTextSize}>{t('adminAiCosts.loading')}</span>
          </div>
        )}

        {error && !loading && (
          <div className={cn(cardClass, 'p-6 text-red-600 dark:text-red-400', smallTextSize)}>{error}</div>
        )}

        {!loading && !error && costs && (
          <>
            {/* Overview — the source of truth for month totals (all features, from ai_costs). */}
            <div className={cn(cardClass, 'p-6')}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label={t('adminAiCosts.totalCost')} value={eur(totalCost)} sub={month} />
                <StatTile label={t('adminAiCosts.calls')} value={num(totals?.calls ?? 0)} />
                <StatTile
                  label={t('adminAiCosts.tokens')}
                  value={num(totalTokens)}
                  sub={t('adminAiCosts.tokensSub', {
                    input: num(totals?.inputTokens ?? 0),
                    output: num(totals?.outputTokens ?? 0),
                  })}
                />
                <StatTile
                  label={t('adminAiCosts.topFeature')}
                  value={topFeature ? featureLabel(topFeature) : '—'}
                  sub={topFeature ? eur(costs.byFeature[0].cost) : undefined}
                />
              </div>
            </div>

            {/* By feature. */}
            <div className={cn(cardClass, 'p-6')}>
              <h3 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.byFeatureTitle')}</h3>
              {costs.byFeature.length === 0 ? (
                <div className={cn(secondaryText, smallTextSize)}>{t('adminAiCosts.noData')}</div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                      <th className="py-2">{t('adminAiCosts.feature')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.share')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.inOutTokens')}</th>
                      <th className="py-2">{t('adminAiCosts.models')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.byFeature.map((f) => (
                      <tr key={f.feature} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                        <td className={cn('py-2', primaryText, smallTextSize)}>{featureLabel(f.feature)}</td>
                        <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>{eur(f.cost)}</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{costSharePct(f.cost)}%</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{num(f.calls)}</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                          {num(f.inputTokens)} / {num(f.outputTokens)}
                        </td>
                        <td className={cn('py-2', secondaryText, 'text-[11px]')}>{f.models.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* By model. */}
            <div className={cn(cardClass, 'p-6')}>
              <h3 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.byModelTitle')}</h3>
              {costs.byModel.length === 0 ? (
                <div className={cn(secondaryText, smallTextSize)}>{t('adminAiCosts.noData')}</div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                      <th className="py-2">{t('adminAiCosts.model')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.share')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.inOutTokens')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.byModel.map((m) => (
                      <tr key={m.model} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                        <td className={cn('py-2 font-mono', primaryText, 'text-[11px]')}>{m.model}</td>
                        <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>{eur(m.cost)}</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{costSharePct(m.cost)}%</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{num(m.calls)}</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                          {num(m.inputTokens)} / {num(m.outputTokens)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* By restaurant (top N + Unattributed). */}
            <div className={cn(cardClass, 'p-6')}>
              <h3 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.byRestaurantTitle')}</h3>
              {restaurantCostRows.length === 0 ? (
                <div className={cn(secondaryText, smallTextSize)}>{t('adminAiCosts.noData')}</div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                      <th className="py-2">{t('adminAiCosts.restaurant')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.share')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurantCostRows.map((r) => (
                      <tr
                        key={r.restaurantId ?? 'unattributed'}
                        className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                      >
                        <td className={cn('py-2', primaryText, smallTextSize)}>
                          {r.name ?? t('adminAiCosts.unattributed')}
                        </td>
                        <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>{eur(r.cost)}</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{costSharePct(r.cost)}%</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{num(r.calls)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* By day. */}
            <div className={cn(cardClass, 'p-6')}>
              <h3 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.byDayTitle')}</h3>
              {costs.byDay.length === 0 ? (
                <div className={cn(secondaryText, smallTextSize)}>{t('adminAiCosts.noData')}</div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                      <th className="py-2">{t('adminAiCosts.day')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                      <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.byDay.map((d) => (
                      <tr key={d.date} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                        <td className={cn('py-2 tabular-nums', primaryText, smallTextSize)}>{d.date}</td>
                        <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>{eur(d.cost)}</td>
                        <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>{num(d.calls)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Voice agent — the previous page's tables verbatim, still fed by the inventory blob. */}
            <div className={cn(cardClass, 'p-6')}>
              <button
                onClick={() => setVoiceOpen((o) => !o)}
                className="flex items-center gap-2 w-full text-left"
              >
                {voiceOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <h3 className={cn(primaryText, semiBoldText)}>{t('adminAiCosts.voiceSectionTitle')}</h3>
              </button>
              <p className={cn(secondaryText, 'mt-1 ml-6', 'text-[11px]')}>{t('adminAiCosts.voiceSectionNote')}</p>

              {voiceOpen && voice && (
                <div className="mt-4 space-y-6">
                  <div className="flex items-center gap-2">
                    <select
                      value={restaurantFilter}
                      onChange={(e) => setRestaurantFilter(e.target.value)}
                      className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border-none', primaryText, smallTextSize)}
                    >
                      <option value="all">{t('adminAiCosts.allRestaurants')}</option>
                      {restaurantRows.map(([id]) => (
                        <option key={id} value={id}>{nameFor(id)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Voice totals. */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatTile
                      label={t('adminAiCosts.voice.totalCost')}
                      value={usd(voiceTotalCost)}
                      sub={restaurantFilter === 'all' ? month : nameFor(restaurantFilter)}
                    />
                    <StatTile label={t('adminAiCosts.calls')} value={num(voiceTotal?.calls ?? 0)} />
                    <StatTile
                      label={t('adminAiCosts.tokens')}
                      value={num((voiceTotal?.inputTokens ?? 0) + (voiceTotal?.outputTokens ?? 0))}
                      sub={t('adminAiCosts.voice.cacheReadSub', { count: voiceTotal?.cacheReadTokens ?? 0 })}
                    />
                    <StatTile
                      label={t('adminAiCosts.voice.audio')}
                      value={`${Math.round((voiceTotal?.audioSeconds ?? 0) / 60)}m`}
                      sub={t('adminAiCosts.voice.whisperSub')}
                    />
                    <StatTile
                      label={t('adminAiCosts.voice.avgPerCall')}
                      value={voiceTotal && voiceTotal.calls > 0 ? usd(voiceTotal.costUsd / voiceTotal.calls) : '—'}
                    />
                  </div>

                  {/* Where the money actually goes. */}
                  <div>
                    <h4 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.voice.bySourceTitle')}</h4>
                    <table className="w-full text-left">
                      <thead>
                        <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                          <th className="py-2">{t('adminAiCosts.voice.source')}</th>
                          <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                          <th className="py-2 text-right">{t('adminAiCosts.share')}</th>
                          <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                          <th className="py-2 text-right">{t('adminAiCosts.voice.usage')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SOURCE_ORDER.map((source) => {
                          const s = scoped?.bySource[source];
                          return (
                            <tr key={source} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                              <td className={cn('py-2', primaryText, smallTextSize)}>
                                {sourceLabel(source)}
                                <span className={cn('block', secondaryText, 'text-[10px]')}>
                                  {t(`adminAiCosts.sourceHints.${source}`)}
                                </span>
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>
                                {usd(s?.costUsd ?? 0)}
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                {s ? `${voiceSharePct(s.costUsd)}%` : '—'}
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                {num(s?.calls ?? 0)}
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                {source === 'voice_transcribe'
                                  ? `${Math.round((s?.audioSeconds ?? 0) / 60)}m ${t('adminAiCosts.voice.audioUnit')}`
                                  : `${num((s?.inputTokens ?? 0) + (s?.outputTokens ?? 0))} ${t('adminAiCosts.voice.tokensUnit')}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Per restaurant. */}
                  <div>
                    <h4 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.voice.byRestaurantTitle')}</h4>
                    {restaurantRows.length === 0 ? (
                      <div className={cn(secondaryText, smallTextSize)}>{t('adminAiCosts.noData')}</div>
                    ) : (
                      <table className="w-full text-left">
                        <thead>
                          <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                            <th className="py-2">{t('adminAiCosts.restaurant')}</th>
                            <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                            <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                            {SOURCE_ORDER.map((s) => (
                              <th key={s} className="py-2 text-right">{sourceLabel(s)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {restaurantRows.map(([id, breakdown]) => (
                            <tr
                              key={id}
                              onClick={() => setRestaurantFilter(id)}
                              className={cn(
                                'border-b border-gray-100 dark:border-gray-800 last:border-0 cursor-pointer',
                                'hover:bg-gray-50 dark:hover:bg-gray-800/40',
                                restaurantFilter === id && 'bg-purple-50 dark:bg-purple-900/20'
                              )}
                            >
                              <td className={cn('py-2', primaryText, smallTextSize)}>{nameFor(id)}</td>
                              <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>
                                {usd(breakdown.total.costUsd)}
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                {num(breakdown.total.calls)}
                              </td>
                              {SOURCE_ORDER.map((s) => (
                                <td key={s} className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                  {breakdown.bySource[s] ? usd(breakdown.bySource[s]!.costUsd) : '—'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* What one counting session cost. */}
                  <div>
                    <h4 className={cn(primaryText, semiBoldText, 'mb-3')}>{t('adminAiCosts.voice.bySessionTitle')}</h4>
                    {sessionRows.length === 0 ? (
                      <div className={cn(secondaryText, smallTextSize)}>{t('adminAiCosts.voice.noSessions')}</div>
                    ) : (
                      <table className="w-full text-left">
                        <thead>
                          <tr className={cn(secondaryText, 'text-[10px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700')}>
                            <th className="py-2">{t('adminAiCosts.voice.session')}</th>
                            <th className="py-2">{t('adminAiCosts.restaurant')}</th>
                            <th className="py-2 text-right">{t('adminAiCosts.cost')}</th>
                            <th className="py-2 text-right">{t('adminAiCosts.calls')}</th>
                            <th className="py-2 text-right">{t('adminAiCosts.voice.lastActivity')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sessionRows.map(([id, session]) => (
                            <tr key={id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                              <td className={cn('py-2 font-mono', secondaryText, 'text-[11px]')}>{id.slice(0, 8)}</td>
                              <td className={cn('py-2', primaryText, smallTextSize)}>{nameFor(session.restaurantId)}</td>
                              <td className={cn('py-2 text-right tabular-nums', semiBoldText, primaryText, smallTextSize)}>
                                {usd(session.total.costUsd)}
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                {num(session.total.calls)}
                              </td>
                              <td className={cn('py-2 text-right tabular-nums', secondaryText, smallTextSize)}>
                                {session.total.lastAt ? new Date(session.total.lastAt).toLocaleString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAiCostsPage;
